import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  BarChart3,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Download,
  Upload
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { mockStrategies } from '../utils/mockData'
import Toast from '../components/Toast'

export default function DemoStrategiesPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [showToast, setShowToast] = useState(false)

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'trend':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      case 'grid':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'mean_reversion':
        return 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
      case 'breakout':
        return 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
      case 'scalping':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const filteredStrategies = mockStrategies.filter(strategy => {
    const matchesSearch = strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         strategy.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || strategy.type === filterType
    const matchesStatus = filterStatus === 'all' || strategy.status === filterStatus
    return matchesSearch && matchesType && matchesStatus
  })

  const sortedStrategies = [...filteredStrategies].sort((a, b) => {
    let aValue, bValue
    
    switch (sortBy) {
      case 'name':
        aValue = a.name
        bValue = b.name
        break
      case 'type':
        aValue = a.type
        bValue = b.type
        break
      case 'status':
        aValue = a.status
        bValue = b.status
        break
      case 'created_at':
      default:
        aValue = new Date(a.created_at).getTime()
        bValue = new Date(b.created_at).getTime()
        break
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/demo')}
                className="btn-secondary flex items-center justify-center"
                title="返回主页"
              >
                <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">返回主页</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gradient">策略管理</h1>
                <p className="text-ocean-secondary">管理和监控您的所有交易策略</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button className="btn-secondary flex items-center justify-center" title="导入策略">
              <Upload className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">导入策略</span>
            </button>
            <button 
              onClick={() => navigate('/demo/new-strategy')}
              className="btn-primary flex items-center justify-center"
              title="新建策略"
            >
              <Plus className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">新建策略</span>
            </button>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">总策略数</p>
                <p className="text-2xl font-bold text-gradient">{mockStrategies.length}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <Play className="w-6 h-6 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">实盘中</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'live').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                <Pause className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">验证中</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'test').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-gray-500/20 rounded-xl border border-gray-500/30">
                <Eye className="w-6 h-6 text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">草稿</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockStrategies.filter(s => s.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 搜索和筛选 */}
        <div className="card mb-6">
          <div className="flex flex-col gap-4">
            {/* 搜索框 - 始终显示 */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-ocean-secondary w-5 h-5" />
              <input
                type="text"
                placeholder="搜索策略名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            
            {/* 筛选器 - 小屏幕下隐藏，大屏幕显示 */}
            <div className="hidden lg:flex items-center gap-3">
              <Filter className="w-5 h-5 text-ocean-secondary" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">所有类型</option>
                <option value="trend">趋势跟踪</option>
                <option value="grid">网格交易</option>
                <option value="mean_reversion">均值回归</option>
                <option value="breakout">突破策略</option>
                <option value="scalping">高频交易</option>
                <option value="other">其他</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">所有状态</option>
                <option value="draft">草稿</option>
                <option value="test">验证中</option>
                <option value="live">实盘中</option>
              </select>
              
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-')
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="created_at-desc">最新创建</option>
                <option value="created_at-asc">最早创建</option>
                <option value="name-asc">名称 A-Z</option>
                <option value="name-desc">名称 Z-A</option>
                <option value="type-asc">类型 A-Z</option>
                <option value="status-asc">状态 A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* 策略列表 */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-ocean-primary">
              策略列表 ({sortedStrategies.length})
            </h3>
            <button className="btn-secondary flex items-center">
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">导出</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-4 text-ocean-secondary font-medium">策略名称</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">类型</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">交易对</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">时间周期</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">状态</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">创建时间</th>
                  <th className="text-left p-4 text-ocean-secondary font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {sortedStrategies.map((strategy) => (
                  <tr key={strategy.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <div className="text-ocean-primary font-medium">{strategy.name}</div>
                      <div className="text-sm text-ocean-secondary line-clamp-2">
                        {strategy.description}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(strategy.type)}`}>
                        {getTypeLabel(strategy.type)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="text-ocean-primary font-medium">{strategy.market_symbol}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-ocean-primary font-medium">{strategy.timeframe}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                        {getStatusLabel(strategy.status)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-ocean-secondary">
                        {new Date(strategy.created_at).toLocaleDateString('zh-CN')}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => setShowToast(true)}
                          className="text-violet-300 hover:text-violet-400 p-1"
                          title="查看详情"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-green-300 hover:text-green-400 p-1" title="编辑">
                          <Edit className="w-4 h-4" />
                        </button>
                        {strategy.status === 'live' ? (
                          <button className="text-yellow-300 hover:text-yellow-400 p-1" title="暂停">
                            <Pause className="w-4 h-4" />
                          </button>
                        ) : (
                          <button className="text-green-300 hover:text-green-400 p-1" title="启动">
                            <Play className="w-4 h-4" />
                          </button>
                        )}
                        <button className="text-red-300 hover:text-red-400 p-1" title="删除">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sortedStrategies.length === 0 && (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-ocean-secondary mx-auto mb-4" />
              <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无策略</h3>
              <p className="text-ocean-secondary mb-6">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all'
                  ? '没有找到匹配的策略，请调整搜索条件' 
                  : '开始创建您的第一个交易策略'
                }
              </p>
              {!searchTerm && filterType === 'all' && filterStatus === 'all' && (
                <button 
                  onClick={() => navigate('/demo/new-strategy')}
                  className="btn-primary flex items-center mx-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  新建策略
                </button>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Toast */}
      <Toast
        message="Demo仅做展示，详细功能请查看下方功能导航"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </div>
  )
} 